import { IonContent, IonLoading, IonPage, useIonToast } from '@ionic/react';
import { MenuItem, Select } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import axios from '../../axios';
import Header from '../../components/Header';

const EDITABLE_FIELDS = [
    { field: 'document_name', label: 'Documents.document_name', type: 'text', required: true, requiredText: 'Document Name is required', options: undefined as string[] | undefined },
    { field: 'document_number', label: 'Documents.document_number', type: 'text', required: true, requiredText: 'Document Number is required', options: undefined as string[] | undefined },
    {
        field: 'status', label: 'Documents.status', type: 'select', required: true, requiredText: 'Please select status',
        options: ['Compliant', 'In Progress', 'Completed', 'Non Compliant', 'Need Renewal', 'Need Inspection']
    },
    {
        field: 'severity', label: 'Documents.severity', type: 'select', required: false, requiredText: '',
        options: ['Low', 'Medium', 'High']
    },
    { field: 'issued_date', label: 'Documents.issued_date', type: 'date', required: true, requiredText: 'Please select issued date', options: undefined as string[] | undefined },
    { field: 'expiry_date', label: 'Documents.expiry_date', type: 'date', required: false, requiredText: '', options: undefined as string[] | undefined },
    { field: 'issuing_entity', label: 'Documents.issuing_entity', type: 'text', required: false, requiredText: '', options: undefined as string[] | undefined },
    { field: 'days_to_renewal', label: 'Documents.days_to_renewal', type: 'number', required: false, requiredText: '', options: undefined as string[] | undefined },
    { field: 'reminder_days', label: 'Documents.reminder_days', type: 'number', required: false, requiredText: '', options: undefined as string[] | undefined },
];

const FETCH_FIELDS = [
    'document_name', 'document_number', 'status', 'severity',
    'issued_date', 'expiry_date', 'issuing_entity', 'days_to_renewal', 'reminder_days',
    'document_category', 'document_type',
    'assigned_projects', 'assigned_assets', 'assigned_users',
];

const dropdownStyle: React.CSSProperties = {
    position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
    border: '1px solid #ccc', zIndex: 10, boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    maxHeight: '150px', overflowY: 'auto',
};
const dropdownItemStyle: React.CSSProperties = {
    padding: '10px', borderBottom: '1px solid #eee', color: 'black', cursor: 'pointer',
};
const chipStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', background: '#e0e0e0',
    borderRadius: '16px', padding: '4px 12px', fontSize: '12px',
};

const EditDocument: React.FC = () => {
    const router = useHistory();
    const [present] = useIonToast();
    const { t } = useTranslation();
    const location = useLocation();
    const documentName = location.state as string;

    const [showLoading, setShowLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [values, setValues] = useState<Record<string, any>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    // ── Pre-Fetch Options Globally ────────────────────────────────────────────
    const [allCats, setAllCats] = useState<string[]>([]);
    const [allTypes, setAllTypes] = useState<string[]>([]);
    const [allProjs, setAllProjs] = useState<string[]>([]);
    const [allAssets, setAllAssets] = useState<string[]>([]);
    const [allUsers, setAllUsers] = useState<string[]>([]);

    useEffect(() => {
        const fetchAllOptions = async () => {
            try {
                const getBasicAuth = () => `Basic ${localStorage.getItem('hyprism_login')}`;
                const [resCat, resType, resProj, resAsset, resUser] = await Promise.all([
                    axios({ method: 'get', url: `/resource/Compliance document category?limit_page_length=500`, headers: { Authorization: getBasicAuth() } }),
                    axios({ method: 'get', url: `/resource/Compliance document type?limit_page_length=500`, headers: { Authorization: getBasicAuth() } }),
                    axios({ method: 'get', url: `/resource/Project?limit_page_length=500`, headers: { Authorization: getBasicAuth() } }),
                    axios({ method: 'get', url: `/resource/Asset?limit_page_length=500`, headers: { Authorization: getBasicAuth() } }),
                    axios({ method: 'get', url: `/resource/User?limit_page_length=500&filters=[["enabled","=",1]]`, headers: { Authorization: getBasicAuth() } }),
                ]);
                setAllCats(resCat?.data?.data?.map((item: any) => item.name) || []);
                setAllTypes(resType?.data?.data?.map((item: any) => item.name) || []);
                setAllProjs(resProj?.data?.data?.map((item: any) => item.project_name || item.name) || []);
                setAllAssets(resAsset?.data?.data?.map((item: any) => item.name) || []);
                setAllUsers(resUser?.data?.data?.map((item: any) => item.name) || []);
            } catch (err) {
                console.log('Error pre-fetching document select types', err);
            }
        };
        fetchAllOptions();
    }, []);

    const [selectedProject, setSelectedProject] = useState('');
    const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    // ── Fetch existing document ───────────────────────────────────────────────
    useEffect(() => {
        if (!documentName) { 
            router.replace('/documents');
            return; 
        }
        const load = async () => {
            setIsFetching(true);
            try {
                const fields = encodeURIComponent(JSON.stringify(FETCH_FIELDS));
                const res = await axios({
                    method: 'get',
                    url: `/resource/Compliance%20Documents/${documentName}?fields=${fields}`,
                    headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
                });
                const doc = res?.data?.data || {};
                setValues(doc);
                // Pre-populate assignment fields from child table arrays
                const proj = (doc.assigned_projects || []).map((r: any) => r.project).filter(Boolean);
                setSelectedProject(proj[0] || '');
                setSelectedAssets((doc.assigned_assets || []).map((r: any) => r.asset).filter(Boolean));
                setSelectedUsers((doc.assigned_users || []).map((r: any) => r.user).filter(Boolean));
            } catch (e) {
                present({ message: t('Utils.fetchF', 'Failed to load document'), color: 'danger', duration: 2000 });
            } finally {
                setIsFetching(false);
            }
        };
        load();
    }, [documentName]);

    // ── Fetch helpers (Removed in favor of pre-fetch) ─────────────────────────
    // ── Field handlers ────────────────────────────────────────────────────────
    const set = (field: string, value: any) => {
        setValues((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
    };
    const validate = () => {
        const newErrors: Record<string, string> = {};
        EDITABLE_FIELDS.forEach(({ field, required, requiredText }) => { if (required && !values[field]) newErrors[field] = requiredText; });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ── File upload ───────────────────────────────────────────────────────────
    const handleFileUpload = async (event: any) => {
        const file = event.target.files[0];
        if (!file) return;

        setShowLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('is_private', '0');
            formData.append('folder', 'Home');

            const uploadUrl =
                '/method/upload_file' +
                '?fieldname=attachment' +
                '&attached_to_field=attachment' +
                '&attached_to_doctype=Compliance%20Documents';

            const baseURL = localStorage.getItem('baseUrl') || process.env.REACT_APP_BASE_URL || 'https://admin.hyprism.com/api';
            const fetchUrl = uploadUrl.startsWith('http')
                ? uploadUrl
                : `${baseURL.replace(/\/+$/, '')}/${uploadUrl.replace(/^\/+/, '')}`;

            const response = await fetch(fetchUrl, {
                method: 'POST',
                headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
                body: formData,
            });

            if (!response.ok && response.status !== 200) throw new Error('Upload failed');
            const resData = await response.json();
            const fileUrl = resData?.message?.file_url;

            if (fileUrl) {
                set('attachment', fileUrl);
                present({ message: t('Documents.uploadSuccess', 'File uploaded successfully'), duration: 2000 });
            }
        } catch (error) {
            console.log('File upload error:', error);
            present({ message: t('Documents.uploadFailed', 'Failed to upload file'), duration: 2500, color: 'danger' });
        } finally {
            setShowLoading(false);
        }
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validate()) return;
        setShowLoading(true);
        try {
            const payload: any = {};
            EDITABLE_FIELDS.forEach(({ field }) => { if (values[field] !== undefined && values[field] !== '') payload[field] = values[field]; });
            if (values.document_category) payload.document_category = values.document_category;
            if (values.document_type) payload.document_type = values.document_type;
            if (values.attachment) payload.attachment = values.attachment;
            if (selectedProject) payload.assigned_projects = [{ project: selectedProject }];
            if (selectedAssets.length > 0) payload.assigned_assets = selectedAssets.map((a) => ({ asset: a }));
            if (selectedUsers.length > 0) payload.assigned_users = selectedUsers.map((u) => ({ user: u }));

            await axios({
                method: 'put',
                url: `/resource/Compliance%20Documents/${documentName}`,
                headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}`, 'Content-Type': 'application/json' },
                data: payload,
            });
            present({ message: 'Document updated successfully', duration: 2000, color: 'success' });
            router.push('/documents/view', documentName);
        } catch (e: any) {
            console.log('Update error', e);
            present({ message: 'Failed to update document', duration: 2500, color: 'danger' });
        } finally { setShowLoading(false); }
    };

    if (isFetching) {
        return (
            <IonPage>
                <Header enableBackButton="/documents/view" title={t('Documents.edit', 'Edit Document')} />
                <IonContent><IonLoading isOpen message="Loading…" /></IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <Header enableBackButton="/documents/view" title={t('Documents.edit', 'Edit Document')} />
            <IonContent>
                <IonLoading isOpen={showLoading} onDidDismiss={() => setShowLoading(false)} message={t('Utils.pleasewait', 'Please wait…')} duration={5000} />

                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                    {/* ── Basic Info ── */}
                    <p className="form-title">{t('Documents.basicInfo', 'Document Information')}</p>

                    {EDITABLE_FIELDS.map(({ field, label, type, required, options }) => (
                        <div key={field} className="input-container">
                            <label className="label">{t(label, field)}{required ? '*' : null}</label>
                            {type === 'select' ? (
                                <Select className="custom-select" name={field} value={values[field] || ''} onChange={(e) => set(field, e.target.value as string)}>
                                    {(options || []).map((o: string) => (
                                        <MenuItem key={o} value={o}>
                                            <section style={{ display: 'flex', height: '1rem' }}>
                                                <p style={{ color: 'rgba(31,87,148,1)', marginLeft: '0.2rem', fontSize: '12px', marginTop: '0px' }}>{o}</p>
                                            </section>
                                        </MenuItem>
                                    ))}
                                </Select>
                            ) : type === 'date' ? (
                                <input className="date" type="date" value={values[field] || ''} onChange={(e) => set(field, e.target.value)} />
                            ) : (
                                <input className="basic-input" type={type} placeholder={t(label, field)} value={values[field] || ''} onChange={(e) => set(field, e.target.value)} />
                            )}
                            {errors[field] && <p style={{ color: '#f56954', fontSize: '12px', marginTop: 0 }}>{errors[field]}</p>}
                        </div>
                    ))}

                    {/* ── Document Category ── */}
                    <div className="input-container">
                        <label className="label">{t('Documents.document_category', 'Document Category')}</label>
                        <Select className="custom-select" style={{ fontSize: '12px' }} value={values.document_category || ''} onChange={(e) => set('document_category', e.target.value as string)}>
                            {allCats.map((c: string) => (
                                <MenuItem key={c} value={c}>
                                    <section style={{ display: 'flex', height: '1rem' }}><p style={{ color: 'rgba(31,87,148,1)', marginLeft: '0.2rem', fontSize: '12px', marginTop: '0px' }}>{c}</p></section>
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    {/* ── Document Type ── */}
                    <div className="input-container">
                        <label className="label">{t('Documents.document_type', 'Document Type')}</label>
                        <Select className="custom-select" style={{ fontSize: '12px' }} value={values.document_type || ''} onChange={(e) => set('document_type', e.target.value as string)}>
                            {allTypes.map((t: string) => (
                                <MenuItem key={t} value={t}>
                                    <section style={{ display: 'flex', height: '1rem' }}><p style={{ color: 'rgba(31,87,148,1)', marginLeft: '0.2rem', fontSize: '12px', marginTop: '0px' }}>{t}</p></section>
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    {/* ── Attachment ── */}
                    <div className="input-container">
                        <label className="label">{t('Documents.attachment', 'Attachment')}</label>
                        <input
                            type="file"
                            className="date"
                            id="attachment"
                            onChange={handleFileUpload}
                            style={{ paddingTop: '10px' }}
                        />
                        {values.attachment && (
                            <p style={{ marginTop: '8px', fontSize: '12px', color: '#1d508d' }}>
                                {t('Documents.currentFile', 'Current File')}: {values.attachment.split('/').pop()}
                            </p>
                        )}
                    </div>

                    {/* ── Assignment Info ── */}
                    <p className="form-title">{t('Documents.assignmentInfo', 'Assignment Information')}</p>

                    {/* Project (single) */}
                    <div className="input-container">
                        <label className="label">{t('Documents.assigned_projects_input', 'Assigned Project')}</label>
                        <Select className="custom-select" style={{ fontSize: '12px' }} value={selectedProject || ''} onChange={(e) => setSelectedProject(e.target.value as string)}>
                            {allProjs.map((p: string) => (
                                <MenuItem key={p} value={p}>
                                    <section style={{ display: 'flex', height: '1rem' }}><p style={{ color: 'rgba(31,87,148,1)', marginLeft: '0.2rem', fontSize: '12px', marginTop: '0px' }}>{p}</p></section>
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    {/* Assets (multi) */}
                    <div className="input-container">
                        <label className="label">{t('Documents.assigned_assets_input', 'Assigned Assets')}</label>
                        <Select className="custom-select" style={{ fontSize: '12px' }} multiple value={selectedAssets} onChange={(e) => setSelectedAssets(e.target.value as string[])}
                            renderValue={(selected: any) => {
                                if (Array.isArray(selected)) {
                                    return (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', overflow: 'hidden' }}>
                                            {selected.map((val) => (
                                                <div key={val} style={{ background: 'rgba(31, 87, 148, 0.1)', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', color: '#1F5794', border: '1px solid rgba(31, 87, 148, 0.2)' }}>{val}</div>
                                            ))}
                                        </div>
                                    );
                                }
                                return selected;
                            }}
                        >
                            {allAssets.map((a: string) => (
                                <MenuItem key={a} value={a}>
                                    <section style={{ display: 'flex', height: '1rem' }}><p style={{ color: 'rgba(31,87,148,1)', marginLeft: '0.2rem', fontSize: '12px', marginTop: '0px' }}>{a}</p></section>
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    {/* Users (multi) */}
                    <div className="input-container">
                        <label className="label">{t('Documents.assigned_users_input', 'Assigned Users')}</label>
                        <Select className="custom-select" style={{ fontSize: '12px' }} multiple value={selectedUsers} onChange={(e) => setSelectedUsers(e.target.value as string[])}
                            renderValue={(selected: any) => {
                                if (Array.isArray(selected)) {
                                    return (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', overflow: 'hidden' }}>
                                            {selected.map((val) => (
                                                <div key={val} style={{ background: 'rgba(31, 87, 148, 0.1)', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', color: '#1F5794', border: '1px solid rgba(31, 87, 148, 0.2)' }}>{val}</div>
                                            ))}
                                        </div>
                                    );
                                }
                                return selected;
                            }}
                        >
                            {allUsers.map((u: string) => (
                                <MenuItem key={u} value={u}>
                                    <section style={{ display: 'flex', height: '1rem' }}><p style={{ color: 'rgba(31,87,148,1)', marginLeft: '0.2rem', fontSize: '12px', marginTop: '0px' }}>{u}</p></section>
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    <button className="submit-button" type="submit">{t('Documents.update', 'Update Document')}</button>
                    <div style={{ marginBottom: '1rem' }} />
                </form>
            </IonContent>
        </IonPage>
    );
};

export default EditDocument;
